import { EmailValue, LoanPartiesEmailTo } from 'components/EmailTo'
import type { IDrawRequest } from 'pages/Borrower/DrawRequest/type'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { sendDrawApprovalToBorrower } from 'services'
import { Input2, Modal } from 'stories/components'
import { formatTime, getPrice2decimal } from 'utils'

/**
 * Send the borrower what was approved on their draw request.
 *
 * The document is the thing the customer asked for by name: a table of requested against approved
 * per line, with the reason on anything cut, so the borrower knows what is funded before they sign
 * anything. It goes out as a PDF attachment and as a link they can acknowledge, and the copy that
 * was sent is filed on the loan.
 *
 * Sending again is allowed and is not an error: a chased email, or a second borrower on the loan.
 * It keeps the first link working and keeps an acknowledgement that has already been given.
 */
export const SendDrawApprovalModal = ({
  request,
  loanNumber,
  onClose,
  onSent,
}: {
  request: IDrawRequest & { id: number }
  loanNumber: number
  onClose: Function
  /** Reread the requests: the panel shows whether this decision has been sent and acknowledged. */
  onSent: Function
}) => {
  const [emails, setEmails] = useState<EmailValue>({})
  const [note, setNote] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const documentation = request?.approval?.current?.documentation
  const money = (value: number) => `$${getPrice2decimal(value, false, true)}`

  const onSubmit = async () => {
    setLoading(true)
    try {
      await sendDrawApprovalToBorrower(request.id, emails as Record<string, string>, note)
      toast('The draw approval has been sent to the borrower', { type: 'info' })
      await onSent()
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen
      title="Send Draw Approval to Borrower"
      titleOkay="Send"
      loading={loading}
      disabled={!Object.values(emails).includes('to')}
      lastUpdatedAt={Date.now()}
      onOk={onSubmit}
      onClose={() => onClose()}
    >
      <div className="w-[40rem] max-w-full">
        <div className="text-[14px] mb-3">
          Draw request #{request.id} - approved{' '}
          <span className="font-variation-settings-600">{money(Number(request.approvedTotal || 0))}</span> of{' '}
          {money(Number(request.requestedTotal || 0))} requested.
        </div>

        {!!documentation?.sentAt && (
          <div className="text-[13px] mb-3 px-3 py-2 rounded bg-yellow-50 border border-yellow-200 text-yellow-800">
            Already sent to {documentation.sentTo?.join(', ') || 'the borrower'} on{' '}
            {formatTime(documentation.sentAt)}
            {documentation.acknowledgedAt
              ? `, and acknowledged on ${formatTime(documentation.acknowledgedAt)}.`
              : '. Sending again keeps the original link working.'}
          </div>
        )}

        <LoanPartiesEmailTo
          loanNumber={loanNumber}
          value={emails}
          data={[]}
          onChange={setEmails}
          loadParties
          modalId="drawApprovalToBorrower"
        />

        <div className="mt-4">
          <Input2
            type="text"
            title="Note to the borrower (optional)"
            value={note}
            onChange={(value: string) => setNote(value)}
          />
        </div>

        <div className="mt-3 text-[12px] opacity-70">
          The approval PDF is attached to the email and filed on the loan.
        </div>
      </div>
    </Modal>
  )
}
